// Step-2 bill section — simplified to ONE flat chronological list of every
// bill that matches the Dashboard's active view mode (pay-period / monthly /
// next-30-days). The old multi-bucket layout (Past Due, Bills Coming Up with
// Autopay/Manual sub-rows, View All) was redundant since each view mode
// already defines a date window — rendering one list keeps Step 2 scannable
// and matches how users think about "what bills are in this window?".
//
// The header still carries the Reconcile label, "View Pending Bills" pill
// (opens the aggregate pending modal for all accounts), and "Add Bill" pill.
// Past-due bills that haven't cleared yet are included at the top so money
// still owed stays visible — the existing per-row past-due badge preserves
// the visual urgency cue.
import { useMemo } from "react";
import { useState } from "react";
import {
  Plus, ExternalLink, Zap, Check, CalendarClock, Hourglass,
} from "lucide-react";
import { formatDate, formatMoney } from "@/lib/api";
import { backendToUiStatus, effectiveStatus, displayFor } from "@/lib/bills-util";
import BillAmountCell from "@/components/BillAmountCell";
import ReconcileStalenessPill from "@/pages/app/dashboard/ReconcileStalenessPill";
import { PendingDetailModal } from "@/components/pay-period-forecast/PendingDetailModal";

// Session-storage helpers kept so previously-stored bucket state is still
// cleared on logout via `clearStep2BucketState` from AuthContext. No-op now
// that we no longer render toggles.
const OPEN_STATE_KEY = "wyb:step2-buckets-open";
export function clearStep2BucketState() {
  try { sessionStorage.removeItem(OPEN_STATE_KEY); } catch { /* noop */ }
}

function BillActionRow({ b, band, disp, periodStart, today, onBillUpdated, onReschedule, setBillStatus }) {
  const ui = backendToUiStatus(b.status);
  const eff = effectiveStatus(b, today);
  const dueDate = new Date(b.due_date + "T00:00:00");
  const daysUntil = Math.round((dueDate - today) / 86400000);
  const isPastDue = eff === "unpaid" && daysUntil < 0;
  const isDueToday = eff === "unpaid" && daysUntil === 0;
  const isImminent = eff === "unpaid" && daysUntil >= 1 && daysUntil <= 2;
  const styleFor = {
    Unpaid: "bg-[#F2EFEB] text-[#59605C]",
    Pending: "status-short",
    Paid: "status-safe",
  }[ui];
  const bandCls = isPastDue
    ? "bg-[#F8DAD0] border-l-4 border-l-[#7A2E24]"
    : isDueToday
      ? "bg-[#F8DAD0] border-l-4 border-l-[#C65D3D]"
      : isImminent
        ? "bg-[#FDE2D8] border-l-4 border-l-[#D48A6C]"
        : band === "actionable" && disp.notCleared
          ? "bg-[#FDF2F0]/60"
          : band === "paid"
            ? "bg-[#EEF3EC]"
            : band === "waiting"
              ? "bg-[#FBF3E0]"
              : "bg-[#F9F8F6]";
  const nameCls = band === "paid" ? "text-[#3B5B3E] line-through decoration-[#8FBF88]" : "text-[#2C302E]";
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-3 rounded-xl border-2 border-[#D8CFB9] ${bandCls}`}
      data-testid={`step2-bill-${b.id}`}
      data-band={band}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`${nameCls} font-medium`}>{b.name}</span>
          {b.pay_url && (
            <a
              href={b.pay_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3]"
              data-testid={`step2-pay-online-${b.id}`}
            >
              <ExternalLink size={9} /> pay online
            </a>
          )}
          {b.is_autopay && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E]">
              <Zap size={9} /> autopay
            </span>
          )}
          {b.original_due_date && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE7F6] text-[#4A3B7A]"
              title={`Originally due ${b.original_due_date}`}
              data-testid={`step2-rescheduled-${b.id}`}
            >
              <CalendarClock size={9} /> rescheduled
            </span>
          )}
          {band === "actionable" && new Date(b.due_date + "T00:00:00") < periodStart && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#7A2E24] text-white"
              title={`This bill was due before the current window started`}
              data-testid={`step2-past-due-${b.id}`}
            >
              past due
            </span>
          )}
        </div>
        <div className="text-xs text-[#8A918D] mt-0.5">
          Due{" "}
          <button
            type="button"
            className="text-left hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
            onClick={() => onReschedule && onReschedule(b)}
            title="Click to reschedule this bill"
            data-testid={`step2-due-${b.id}`}
          >
            {formatDate(b.due_date)}
          </button>
          {b.is_autopay && effectiveStatus(b, today) === "pending" && (
            <span
              className="ml-2 text-[10px] italic text-[#3B5B3E]"
              title={new Date(b.due_date + "T00:00:00") > today
                ? "Autopay hasn't charged yet — bank shows this as pending until it clears"
                : "Autopay likely charged already — mark Paid once you see it clear"}
            >
              {new Date(b.due_date + "T00:00:00") > today
                ? `· auto · will charge ${formatDate(b.due_date)}`
                : `· auto · charged ${formatDate(b.due_date)}`}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
        {band === "paid" && (
          <span
            className="order-1 sm:order-1 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-heading font-bold uppercase tracking-[0.24em] bg-[#DDE9D8] text-[#1F6B2E] border-2 border-[#1F6B2E]/70 shadow-sm whitespace-nowrap"
            style={{ transform: "rotate(-6deg)" }}
            data-testid={`step2-paid-stamp-${b.id}`}
          >
            <Check size={14} strokeWidth={3} /> Paid
          </span>
        )}
        <span className="order-2 sm:order-3 inline-flex">
          <BillAmountCell
            bill={b}
            size="md"
            testIdPrefix={`step2-amount-${b.id}`}
            onUpdated={onBillUpdated}
          />
        </span>
        <button
          className="order-3 sm:order-4 btn-ghost !px-2 !py-1"
          onClick={() => onReschedule && onReschedule(b)}
          title="Move this instance to a new date without changing the recurring day"
          aria-label="Reschedule due date"
          data-testid={`step2-reschedule-${b.id}`}
        >
          <CalendarClock size={14} />
        </button>
        <select
          value={ui}
          onChange={(e) => setBillStatus(b, e.target.value)}
          data-testid={`step2-status-${b.id}`}
          className={`order-4 sm:order-2 px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-semibold border-0 outline-none appearance-none cursor-pointer pr-6 sm:pr-7 bg-no-repeat ${styleFor}`}
          style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='%23596059'><path d='M5 8l5 5 5-5H5z'/></svg>\")",
            backgroundPosition: "right 0.4rem center",
          }}
        >
          <option value="Unpaid">Unpaid</option>
          <option value="Pending">Pending</option>
          <option value="Paid">Paid</option>
        </select>
      </div>
    </div>
  );
}

export default function Step2BillBuckets({
  bills,
  viewMode,           // "pay-period" | "month" | "next-30"
  periodStart,        // Date
  periodEnd,          // Date
  today,              // Date at 00:00:00
  user,
  onBillUpdated,
  onReschedule,
  setBillStatus,
  onOpenAddBill,
  pendingItems = [],
  pendingTotal = 0,
  onPendingChanged,
}) {
  const [showPending, setShowPending] = useState(false);

  const periodTag = viewMode === "month"
    ? "This Month"
    : viewMode === "next-30"
      ? "Next 30 Days"
      : "This Pay Period";

  // Flat list of every bill that lands in this window, plus past-due
  // carry-in (unpaid bills from earlier periods that haven't cleared) so
  // money still owed stays visible. Chronologically sorted.
  const rows = useMemo(() => {
    return bills
      .filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        if (d > periodEnd) return false;
        if (d >= periodStart) return true;
        // past-due carry-in: show if the bill still has money owed/pending
        if (b.status === "cleared") return false;
        if (b.status === "paid" && !displayFor(b, today).notCleared) return false;
        return true;
      })
      .map((b) => {
        const disp = displayFor(b, today);
        const eff = effectiveStatus(b, today);
        let band = "paid";
        if (eff === "unpaid") band = "actionable";
        else if (eff === "pending" || (eff === "paid" && disp.notCleared)) band = "waiting";
        return { b, band, disp };
      })
      .sort((a, b) => a.b.due_date.localeCompare(b.b.due_date));
  }, [bills, periodStart, periodEnd, today]);

  return (
    <div className="pt-3 pb-4 px-4 sm:px-6 bg-white/70 border-t border-white/60" data-testid="step2-bill-buckets">
      {/* Header: Reconcile label + date + View Pending Bills pill on the
          left; Add Bill pill + staleness pill on the right. */}
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div className="min-w-0 flex items-center gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7A4E1F]">Reconcile</div>
            <div className="text-xs text-[#59605C] mt-0.5">
              {formatDate(periodStart.toISOString().slice(0, 10))} – {formatDate(periodEnd.toISOString().slice(0, 10))}
              <span className="ml-2 text-[10px] text-[#8A918D]">({periodTag})</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowPending(true)}
            disabled={pendingItems.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#FBF3E0] text-[#7A4E1F] hover:bg-[#F3E5B8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="step2-view-pending-btn"
            title={pendingItems.length === 0 ? "Nothing pending to clear right now" : "Review bills & charges waiting to clear the bank"}
          >
            <Hourglass size={12} /> View Pending Bills{pendingItems.length > 0 ? ` (${pendingItems.length})` : ""}
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onOpenAddBill}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#4A6148] text-white hover:bg-[#3B5B3E] transition-colors"
            data-testid="step2-add-bill-btn"
          >
            <Plus size={12} /> Add Bill
          </button>
          <ReconcileStalenessPill lastAt={user?.last_reconciled_at} streak={user?.reconciliation_streak || 0} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="rounded-2xl border-2 border-dashed border-[#D8CFB9] bg-white/60 text-sm text-[#59605C] p-4 text-center"
          data-testid="step2-empty"
        >
          No bills in this window.
        </div>
      ) : (
        <div className="space-y-2" data-testid="step2-flat-list">
          {rows.map((entry) => (
            <BillActionRow
              key={entry.b.id}
              b={entry.b}
              band={entry.band}
              disp={entry.disp}
              periodStart={periodStart}
              today={today}
              onBillUpdated={onBillUpdated}
              onReschedule={onReschedule}
              setBillStatus={setBillStatus}
            />
          ))}
        </div>
      )}

      {/* Aggregate pending modal — same visual as the per-account Step 1 modal
          but groups pending bills + charges across ALL accounts. */}
      {showPending && (
        <PendingDetailModal
          sourceName="All accounts"
          sourceId={null}
          items={pendingItems}
          total={pendingTotal}
          showSourceName
          hideAddForm
          onClose={() => setShowPending(false)}
          onItemRemoved={(item) => { onPendingChanged && onPendingChanged(item, "delete"); }}
          onItemAdded={(created) => { onPendingChanged && onPendingChanged(created, "create"); }}
          onBillCleared={(item) => { onPendingChanged && onPendingChanged(item, "cleared"); }}
          testId="step2-pending-modal"
        />
      )}
    </div>
  );
}
